
// SmartTablePaginator.tsx
import React from 'react';

interface PaginatorProps {
  rowsPerPage: number;
  onRowsPerPageChange: (n: number) => void;
  // Rango mostrado en la UI ("X de Y resultados")
  total: number;
  // Paginación por ventana (array de páginas dinámico)
  pages: number[]; // ej.: [1,2,3] o [4,5,6,7,8,9] según blockSize/rowsPerPage
  activePage: number;
  onPageChange: (page: number) => void;
  // Eventos para mover ventana (delta ±1)
  onPrevWindow: () => void; // cruzar a la ventana anterior
  onNextWindow: () => void; // cruzar a la ventana siguiente
  hasNext: boolean; // N+1: habilita/deshabilita "Siguiente"
  // Para mostrar “X de Y”: cantidad de páginas visibles en la ventana actual
  mainPage: number;
}

const SmartTablePaginator: React.FC<PaginatorProps> = ({
  rowsPerPage, onRowsPerPageChange,
  total,
  pages, activePage, onPageChange,
  onPrevWindow, onNextWindow, hasNext,
  mainPage
}) => {
  // Si no hay páginas, no mostrar
  if (!pages || pages.length === 0) return null;

  // Prev deshabilitado si la ventana incluye 1 (primera ventana) o no hay páginas
  const prevDisabled = pages.includes(1) || pages.length <= 0;

  // Next deshabilitado si no hay N+1
  const nextDisabled = !hasNext;

  const goPrev = () => {
    if (prevDisabled) return;
    onPrevWindow(); // padre setea mainPage-1; SmartTable posiciona en pages[0] tras estabilizar
  };
  const goNext = () => {
    if (nextDisabled) return;
    onNextWindow(); // padre setea mainPage+1; SmartTable posiciona en pages[0] tras estabilizar
  };

  // Rango "start–end de total resultados" con clamp
  const start = (activePage - 1) * rowsPerPage; // índice base para UI
  const end = Math.min(activePage * rowsPerPage, total); // clamp contra total

  return (
    <div className="st-paginator">
      <div className="st-paginator__left">
        <span className="st-paginator__label">Filas por página:</span>
        <select
          className="st-select"
          value={rowsPerPage}
          onChange={(e)=>onRowsPerPageChange(parseInt(e.target.value,10))}
        >
          {[5,10,20,30,60].map((n)=>(<option key={n} value={n}>{n}</option>))}
        </select>
        <span className="st-range">
          {`${start}–${end} de ${!nextDisabled ? "+": ""}${total} resultados`}
        </span>
      </div>

      <div className="st-paginator__spacer"/>

      <div className="st-paginator__right">
        {/* Texto “X de Y” → Y = último número visible de la ventana actual */}
        {/* <span className="st-page">{activePage} de {pages[pages.length-1]}</span> */}

        {/* Prev (mueve ventana) */}
        <button
          className={"st-nav-btn" + (prevDisabled ? " is-disabled" : "")}
          disabled={prevDisabled}
          onClick={goPrev}
          aria-label="Página anterior"
        >
          ‹
        </button>

        {/* Números (navegar dentro de la ventana actual) */}
        {pages.map((p) => (
          <button
            key={p}
            className={"st-page-btn" + (p === activePage ? " is-active" : "")}
            onClick={()=>onPageChange(p)}
          >
            {p}
          </button>
        ))}

        {/* Next (mueve ventana si hay N+1) */}
        <button
          className={"st-nav-btn" + (nextDisabled ? " is-disabled" : "")}
          disabled={nextDisabled}
          onClick={goNext}
          aria-label="Página siguiente"
        >
          ›
        </button>
      </div>
    </div>
  );
};

export default SmartTablePaginator;